export const SCAMWATCH = [
  {
    slug: "the-waiting-buyer",
    number: "01",
    date: "August 8, 2026",
    title: "The Buyer Who Is Already Waiting",
    teaser: "The oldest script in the book: a corporate buyer, ready to pay above market, just as soon as you cover one small fee.",
    takeaways: [
      "No legitimate buyer is ever 'already waiting' for your specific week",
      "The upfront fee is the entire point of the call — the buyer is fiction",
      "Real brokers earn commission at closing, never fees in advance",
      "Hang up, verify independently, and report the number",
    ],
    sections: [
      {
        heading: "How the call actually goes",
        paragraphs: [
          "The phone rings and a warm, professional voice explains that their company works with corporate clients — sometimes 'investors', sometimes 'travel clubs' — who buy timeshare weeks in bulk. And wouldn't you know it: a buyer is looking for exactly your resort, exactly your week, right now. The price they quote is flattering. Often suspiciously close to what you paid years ago.",
          "There's momentum, a case number, sometimes a second 'verification' call from a supposed title company. Everything feels official. Then comes the turn: a fee stands between you and the closing. A transfer fee, a tax, an escrow deposit, a 'Mexican land registry fee' if your resort is abroad. Pay it, and the sale completes within days.",
        ],
      },
      {
        heading: "Why intelligent people fall for it",
        paragraphs: [
          "Because it answers a wish. Most owners who pick up that phone have quietly wanted out for years and felt foolish about the resale value. A stranger offering above-market price doesn't trigger suspicion — it triggers relief. The scammers know this; the script is engineered around that relief.",
          "The professionalism is the second hook. Real-sounding companies, real websites, contracts on letterhead. Every element exists to make the fee feel like a formality rather than the product.",
        ],
      },
      {
        heading: "The tells",
        paragraphs: [
          "Unsolicited contact about a buyer is the whole tell. Legitimate resale brokers do not cold-call owners with waiting purchasers — that is not how property changes hands. Add any request for money before a closing, especially by wire, gift card or crypto, and the case is closed.",
          "Pressure is the confirmation. A real buyer waits a week. A script cannot survive forty-eight hours of due diligence, which is exactly why the offer 'expires Friday'.",
        ],
      },
      {
        heading: "What to do instead",
        paragraphs: [
          "Hang up without anger and without explanation. Note the number and company name, and report them to your state attorney general and the FTC — your report genuinely builds cases. If you're genuinely curious whether your ownership has resale value, Chapter 02 walks you through finding out for free, starting with your own developer's deed-back program.",
          "And if you've already paid: contact your card issuer or bank immediately, keep every document, and read Chapter 04. You are neither the first nor the fool — the script works precisely because it's well-made.",
        ],
      },
    ],
  },
  {
    slug: "the-tax-that-isnt",
    number: "02",
    date: "August 1, 2026",
    title: "The 'Foreign Tax' That Isn't",
    teaser: "A nearly-closed sale, a government fee that must be paid today, and a wire transfer to make the tax office happy. Except there is no tax office.",
    takeaways: [
      "Foreign 'taxes' and 'registry fees' demanded by wire are a fiction",
      "Real closing costs are itemized by a neutral title or escrow company",
      "You can verify any tax claim with the resort or a local attorney in one call",
      "Urgency is the tell: real taxes have deadlines measured in months, not hours",
    ],
    sections: [
      {
        heading: "The setup",
        paragraphs: [
          "This script usually arrives mid-process: you've been talking to a 'broker' for days or weeks about selling your ownership, often one abroad — Mexico is the classic setting. A buyer is secured, a price agreed, paperwork exchanged. Then, just before closing, a new character enters: the tax. A government transfer tax, a capital gains withholding, a registry fee. It must be paid by wire, by you, before the sale can complete.",
          "The amount is calibrated carefully — large enough to be profitable, small enough relative to the promised sale price to feel like a reasonable cost of finishing.",
        ],
      },
      {
        heading: "Why it's believable",
        paragraphs: [
          "Because foreign property transactions genuinely do involve unfamiliar fees, and most owners have never completed one. The script exploits a real knowledge gap with invented specifics: official-looking tax forms, references to actual-sounding government agencies, sometimes even a 'notario' on the call.",
          "Pay the first fee and a second appears — an insurance certificate, a currency-conversion bond. The pattern is called reloading: once a victim pays, they are sold to more aggressive operations as a proven payer.",
        ],
      },
      {
        heading: "The tells",
        paragraphs: [
          "Any fee demanded by wire transfer, directly, before closing, is a tell on its own. Legitimate closing costs are collected and itemized by a neutral, licensed escrow or title company — one you choose and can verify — not by the party selling you the deal.",
          "The second tell is asymmetry: a 'buyer' who cannot possibly pay a small fee at closing from the five-figure proceeds they are supposedly sending you.",
        ],
      },
      {
        heading: "What to do instead",
        paragraphs: [
          "Verify before you wire: call your resort's owner services line and ask whether any such tax exists — they field this question weekly. For a foreign transaction, spend a fraction of the 'fee' on an independent local attorney you find yourself, not one the broker recommends.",
          "And hold the rule that beats every variant: money flows to you at closing, through escrow you chose. Any arrangement that reverses that flow is not a sale.",
        ],
      },
    ],
  },
  {
    slug: "the-impersonator",
    number: "03",
    date: "July 25, 2026",
    title: "The Caller Pretending to Be Your Resort",
    teaser: "Caller ID says the name of your developer. The voice knows your resort. What they're selling is fear — and a very expensive way out.",
    takeaways: [
      "Developers do not cold-call owners threatening fee consequences",
      "Your ownership records are partly public — familiarity proves nothing",
      "Hang up and call the resort back on the number from your own statements",
      "Never 'verify' yourself to an inbound caller with account or card details",
    ],
    sections: [
      {
        heading: "The call",
        paragraphs: [
          "The caller says they're from your developer's 'owner services' or 'exit department' — sometimes the caller ID even shows a convincing name. They know your resort and often your ownership type. Then the pivot: new fees are coming, your ownership terms are changing, or a 'one-time surrender opportunity' is available today only, for an administrative fee.",
          "Every detail is designed to lower your guard: the brand name you trust, the familiarity with your account, the official vocabulary.",
        ],
      },
      {
        heading: "How they know so much",
        paragraphs: [
          "Timeshare deeds are public record in most jurisdictions, and owner lists circulate endlessly between operations. A caller naming your resort, your unit type, even your purchase year is not demonstrating inside access — they are reading a spreadsheet.",
          "Knowing this single fact defuses the entire performance: familiarity is data, not authority.",
        ],
      },
      {
        heading: "The tells",
        paragraphs: [
          "Real developers communicate important account changes in writing, through the mail and your owner portal — not through pressure calls. Any inbound call that asks for payment, card details, or 'verification' of your identity is behaving like no legitimate owner-services department ever has.",
          "And the offer itself is the tell: a genuine surrender program doesn't expire on a phone call. Deed-back programs are standing policies, not flash sales.",
        ],
      },
      {
        heading: "What to do instead",
        paragraphs: [
          "Take nothing, confirm nothing, and end the call politely. If the claim intrigues you, call your developer back on the number printed on your own maintenance-fee statement — never a number the caller provides. Ask directly whether any such program or fee change exists.",
          "Report the impersonation to your developer's real owner services line, too. They track these operations, and your report helps them warn other owners.",
        ],
      },
    ],
  },
];
